/**
 * Boutique TEXTIPRINT — Stocks (Sprint 1 MVP).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §2.5 B3.
 *
 * Onglet "Stocks" : table avec produit + variante + qté actuelle + réservée
 *                   + dispo + dernières dates + bouton "Ajuster"
 * Onglet "Mouvements" : historique (date, produit, type, qté signée, motif, user)
 *
 * Le dialog "Ajuster" applique POST /stocks/:id/ajuster qui crée en transaction
 * atomique : update stockBoutique.quantiteActuelle + insert MouvementStockBoutique
 * + set derniereEntree/SortieAt selon signe du delta.
 */
import { useState, type FormEvent } from 'react';
import { RefreshCw, AlertTriangle, Boxes, Activity, Plus, Minus } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useStocksBoutique, useAjusterStockBoutique, useMouvementsBoutique,
} from '@/hooks/useBoutique';
import {
  TYPE_MOUVEMENT_BOUTIQUE_LABELS, UNITE_VENTE_BOUTIQUE_LABELS,
  type StockBoutique, type TypeMouvementBoutique,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

type Tab = 'stocks' | 'mouvements';

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
       + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export const BoutiqueStocksPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [tab, setTab] = useState<Tab>('stocks');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<StockBoutique | undefined>();
  const [error, setError] = useState<string | null>(null);

  // Stocks
  const { data: stocksData, isLoading: stocksLoading, refetch: refetchStocks } =
    useStocksBoutique({ limit: 500 });
  const stocks = stocksData?.data ?? [];

  // Mouvements
  const { data: mvtsData, isLoading: mvtsLoading, refetch: refetchMvts } =
    useMouvementsBoutique({ limit: 200 });
  const mouvements = mvtsData?.data ?? [];

  // (useAjusterStockBoutique est instancié dans AjusterStockDialog ci-dessous.)

  // KPIs
  const totalRefStock = stocks.length;
  const totalQuantite = stocks.reduce((s, st) => s + st.quantiteActuelle, 0);
  const totalReservee = stocks.reduce((s, st) => s + st.quantiteReservee, 0);
  const totalDispo    = totalQuantite - totalReservee;

  const openAdjust = (st: StockBoutique) => {
    setError(null);
    setAdjustTarget(st);
    setAdjustOpen(true);
  };

  const mvtTypeBadgeClass = (type: TypeMouvementBoutique) => {
    if (type === 'entree_fournisseur' || type === 'retour_client' || type === 'transfert_production')
      return 'ds-bd ds-bd-s';
    if (type === 'sortie_vente' || type === 'perte') return 'ds-bd ds-bd-d';
    return 'ds-bd ds-bd-info';
  };

  return (
    <>
      <Topbar
        title="Stocks Boutique"
        subtitle={filiale ? `${totalRefStock} référence(s) · ${filiale.fullName}` : 'Stocks boutique'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => { void refetchStocks(); void refetchMvts(); }}>
            <RefreshCw size={14} className={(stocksLoading || mvtsLoading) ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><Boxes size={18} /></div>
            <div className="ds-kr-val">{totalRefStock}</div>
            <div className="ds-kr-lbl">Références en stock</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico">📦</div>
            <div className="ds-kr-val">{formatNumber(totalQuantite)}</div>
            <div className="ds-kr-lbl">Quantité totale</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico">🔒</div>
            <div className="ds-kr-val">{formatNumber(totalReservee)}</div>
            <div className="ds-kr-lbl">Réservée (paniers)</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico">✅</div>
            <div className="ds-kr-val">{formatNumber(totalDispo)}</div>
            <div className="ds-kr-lbl">Disponible vente</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico"><Activity size={18} /></div>
            <div className="ds-kr-val">{mouvements.length}</div>
            <div className="ds-kr-lbl">Mouvements récents</div>
          </div>
        </div>

        <div className="ds-tabs-bar" style={{ marginBottom: 12 }}>
          <button
            className={`ds-tab ${tab === 'stocks' ? 'is-active' : ''}`}
            onClick={() => setTab('stocks')}
          >
            <Boxes size={14} /> Stocks ({totalRefStock})
          </button>
          <button
            className={`ds-tab ${tab === 'mouvements' ? 'is-active' : ''}`}
            onClick={() => setTab('mouvements')}
          >
            <Activity size={14} /> Mouvements ({mouvements.length})
          </button>
        </div>

        {tab === 'stocks' ? (
          <div className="ds-table-panel">
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Stocks par produit</div>
                <div className="ds-table-sub">{totalRefStock} ligne{totalRefStock > 1 ? 's' : ''}</div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Produit (SKU)</th>
                  <th style={{ width: 150 }}>Variante</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Actuelle</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Réservée</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Dispo</th>
                  <th style={{ width: 80 }}>Unité</th>
                  <th style={{ width: 100 }}>Dern. entrée</th>
                  <th style={{ width: 100 }}>Dern. sortie</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {stocksLoading ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
                ) : stocks.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun stock initialisé — utilisez "Ajuster" sur un produit pour créer le premier mouvement</td></tr>
                ) : stocks.map((st) => {
                  const dispo = st.quantiteActuelle - st.quantiteReservee;
                  return (
                    <tr key={st.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{st.produit?.nom ?? '—'}</div>
                        <code style={{ fontSize: 10, color: 'var(--tx-3)' }}>{st.produit?.sku ?? st.produitId.slice(0, 8)}</code>
                      </td>
                      <td>
                        {st.variante ? (
                          <span style={{ fontSize: 12 }}>
                            {[st.variante.taille, st.variante.couleur].filter(Boolean).join(' · ') || st.variante.skuVariante}
                          </span>
                        ) : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {formatNumber(st.quantiteActuelle)}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--tx-3)' }}>
                        {formatNumber(st.quantiteReservee)}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: dispo > 0 ? 'var(--p)' : 'var(--danger, #C0392B)' }}>
                        {formatNumber(dispo)}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                        {st.produit?.unite ? UNITE_VENTE_BOUTIQUE_LABELS[st.produit.unite] : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{formatDate(st.derniereEntreeAt)}</td>
                      <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{formatDate(st.derniereSortieAt)}</td>
                      <td>
                        <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => openAdjust(st)}>
                          ⚡ Ajuster
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ds-table-panel">
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Mouvements de stock</div>
                <div className="ds-table-sub">Audit trail — 200 derniers (immuable sauf admin holding)</div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Date</th>
                  <th>Produit (SKU)</th>
                  <th style={{ width: 160 }}>Type</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Quantité</th>
                  <th>Motif</th>
                  <th style={{ width: 120 }}>Référence</th>
                  <th style={{ width: 130 }}>Par</th>
                </tr>
              </thead>
              <tbody>
                {mvtsLoading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
                ) : mouvements.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun mouvement enregistré</td></tr>
                ) : mouvements.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{formatDateTime(m.createdAt)}</td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{m.produit?.nom ?? '—'}</div>
                      <code style={{ fontSize: 10, color: 'var(--tx-3)' }}>{m.produit?.sku ?? m.produitId.slice(0, 8)}</code>
                    </td>
                    <td><span className={mvtTypeBadgeClass(m.type)}>{TYPE_MOUVEMENT_BOUTIQUE_LABELS[m.type]}</span></td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: m.quantite > 0 ? 'var(--p)' : 'var(--danger, #C0392B)' }}>
                      {m.quantite > 0 ? '+' : ''}{formatNumber(m.quantite)}
                    </td>
                    <td style={{ fontSize: 12 }}>{m.motif ?? '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{m.reference ?? '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{m.user?.fullName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adjustOpen && adjustTarget && (
        <AjusterStockDialog
          stock={adjustTarget}
          onClose={() => setAdjustOpen(false)}
          onError={(msg) => setError(msg)}
          onSuccess={() => {
            setAdjustOpen(false);
            void refetchStocks();
            void refetchMvts();
          }}
        />
      )}
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Dialog Ajuster Stock
// ──────────────────────────────────────────────────────────────────────────
interface AjusterDialogProps {
  stock:     StockBoutique;
  onClose:   () => void;
  onSuccess: () => void;
  onError:   (msg: string) => void;
}

const AjusterStockDialog = ({ stock, onClose, onSuccess, onError }: AjusterDialogProps) => {
  const [sens, setSens] = useState<'+' | '-'>('+');
  const [montant, setMontant] = useState<number>(0);
  const [type, setType] = useState<TypeMouvementBoutique>('ajustement_inventaire');
  const [motif, setMotif] = useState('');
  const [reference, setReference] = useState('');
  const ajuster = useAjusterStockBoutique();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (montant <= 0) { onError('Quantité doit être > 0'); return; }
    const delta = sens === '+' ? montant : -montant;
    try {
      await ajuster.mutateAsync({
        id: stock.id,
        payload: {
          delta,
          motif: motif.trim() || TYPE_MOUVEMENT_BOUTIQUE_LABELS[type],
          type,
          reference: reference.trim() || undefined,
        },
      });
      onSuccess();
    } catch (err) {
      onError(extractError(err).message);
    }
  };

  return (
    <Dialog open onClose={onClose} title={`Ajuster stock — ${stock.produit?.nom ?? stock.produit?.sku ?? 'Produit'}`} size="md">
      <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--tx-3)', padding: 10, background: 'var(--bg-deep)', borderRadius: 6 }}>
          État actuel : <strong>{stock.quantiteActuelle}</strong> en stock · <strong>{stock.quantiteReservee}</strong> réservés
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-2)', marginBottom: 4 }}>Sens *</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className={`ds-btn ${sens === '+' ? 'ds-btn-s' : 'ds-btn-g'}`}
              onClick={() => setSens('+')}
              style={{ flex: 1 }}
            >
              <Plus size={14} /> Entrée
            </button>
            <button
              type="button"
              className={`ds-btn ${sens === '-' ? 'ds-btn-d' : 'ds-btn-g'}`}
              onClick={() => setSens('-')}
              style={{ flex: 1 }}
            >
              <Minus size={14} /> Sortie
            </button>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-2)', marginBottom: 4 }}>Quantité *</label>
          <input
            type="number"
            min={1}
            className="ds-fi"
            value={montant || ''}
            onChange={(e) => setMontant(Number(e.target.value) || 0)}
            required
            autoFocus
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-2)', marginBottom: 4 }}>Type mouvement *</label>
          <select className="ds-fi" value={type} onChange={(e) => setType(e.target.value as TypeMouvementBoutique)}>
            {(Object.keys(TYPE_MOUVEMENT_BOUTIQUE_LABELS) as TypeMouvementBoutique[]).map((t) => (
              <option key={t} value={t}>{TYPE_MOUVEMENT_BOUTIQUE_LABELS[t]}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-2)', marginBottom: 4 }}>Motif (optionnel)</label>
          <input
            type="text"
            className="ds-fi"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Ex: Réception fournisseur Encres Sika"
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-2)', marginBottom: 4 }}>Référence (optionnel)</label>
          <input
            type="text"
            className="ds-fi"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Ex: BL-FOURN-2026-042, TX-SHOP-2026-0042…"
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose} disabled={ajuster.isPending}>
            Annuler
          </button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={ajuster.isPending || montant <= 0}>
            {ajuster.isPending ? 'Application…' : `Appliquer ${sens}${montant || 0}`}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
